import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps
} from 'recharts'
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent'
import './Chart.css'
import CircleIcon from '@mui/icons-material/Circle'
import { useAppSelector } from '~/app/hooks'
import { isDarkTheme, localeSetting } from '~/redux/features'
import { useWindowDimensions } from '~/hooks'
import { ALL_NS } from '~/i18n/namespaces'
import { useTranslation } from 'react-i18next'
import { EFBarChartProps } from './EFCharts.model'
import { LocalTimeText } from '../EFLocalTime'

function percentage(partialValue: number, totalValue: number) {
  return (100 * partialValue) / totalValue
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  if (active) {
    return (
      <div className='custom-tooltip'>
        {payload && (
          <div>
            <p className='label1'>
              <p>{label}</p> <CircleIcon style={{ color: payload[0].color }} />{' '}
              {`${payload[0].dataKey} : ${
                payload[0].value === 'NaN' ? 0 : payload[0].value
              }%`}
            </p>
            <p className='label2'>
              <CircleIcon style={{ color: payload[1].color }} />{' '}
              {`${payload[1].dataKey} : ${
                payload[1].value === 'NaN' ? 0 : payload[1].value
              }%`}
            </p>
            {payload[2] && (
              <p className='label3'>
                <CircleIcon style={{ color: payload[2].color }} />{' '}
                {`${payload[2].dataKey} : ${
                  payload[2].value === 'NaN' ? 0 : payload[2].value
                }%`}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }

  return null
}

export function EFBarChart(props: EFBarChartProps) {
  const { t } = useTranslation(ALL_NS)
  const { isFull, data } = props
  const { height } = useWindowDimensions()
  const localeTime = useAppSelector(localeSetting)
  const darkMode = useAppSelector(isDarkTheme)
  const transformedDataArray: any[] = useMemo(() => {
    return data.map(data => {
      const { date, latestVersionDevices, mandatoryVersionDevices, total } = data
      const latestVersionDevicesPercent = Math.round(
        percentage(latestVersionDevices, total)
      )
      const mandatoryVersionDevicesPercent = Math.round(
        percentage(mandatoryVersionDevices, total)
      )
      const outOfDateDevicesPercent =
        100 - (latestVersionDevicesPercent + mandatoryVersionDevicesPercent)
      const calendarDate = LocalTimeText(
        date,
        'L',
        localeTime.timezone,
        localeTime.locale
      )
      return {
        date: calendarDate,
        [t('Value_DeviceStateOutOfDate')]: outOfDateDevicesPercent.toString(),
        [t('Value_DeviceStateMandatoryVersion')]:
          mandatoryVersionDevicesPercent.toString(),
        [t('Value_DeviceStateLatestVersion')]: latestVersionDevicesPercent.toString()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <ResponsiveContainer
      width='100%'
      height={'100%'}
    >
      <BarChart
        style={{
          width: '100%',
          height: 'auto'
        }}
        id={isFull ? 'barchart-full' : 'barchart'}
        width={isFull ? 1625 : 825}
        height={isFull ? height / 1.5 : height / 1.8}
        data={transformedDataArray}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5
        }}
        barSize={isFull ? 30 : 20}
      >
        <CartesianGrid
          fill={darkMode ? 'var(--dark-gray-background)' : 'var(--white)'}
          vertical={false}
          strokeDasharray='6 6'
        />
        <XAxis
          dataKey='date'
          fontSize='0.8rem'
          axisLine={true}
          stroke={darkMode ? 'white' : '#7A7A7A'}
          label={{
            fontSize: '0.8rem'
          }}
          interval="preserveStartEnd"
          minTickGap={20}
          tickMargin={6}
        />
        <YAxis
          stroke={darkMode ? 'white' : '#7A7A7A'}
          axisLine={true}
          domain={[0, 100]}
          tickCount={6}
          tickMargin={6}
          fontSize='0.8rem'
          label={{
            value: t('Label_UptoDatePercent'),
            angle: -90,
            position: 'insideLeft',
            dy: 30,
            fontSize: '0.8rem',
            fill: darkMode ? 'white' : '#7A7A7A'
          }}
        />
        <Tooltip
          cursor={{ fill: '#05000036' }}
          content={<CustomTooltip />}
        />
        {isFull && (
          <Bar
            animationDuration={250}
            dataKey={t('Value_DeviceStateOutOfDate')}
            fill='#D9D9D9'
          />
        )}
        <Bar
          animationDuration={250}
          dataKey={t('Label_MandatoryVersion')}
          fill='var(--graph-fill-mandatoryversion)'
        />
        <Bar
          animationDuration={250}
          dataKey={t('Value_DeviceStateLatestVersion')}
          fill='var(--graph-fill-latestversion)'
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
